import MomentUtils from '@date-io/moment'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import { loadStripe } from '@stripe/stripe-js'
import React from 'react'
import { useIdleTimer } from 'react-idle-timer'
import { useNavigate } from 'react-router-dom'
import { ToastContainer, Zoom } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import config from 'src/config'
import { AppActions, appReducer, Types } from 'src/reducers/AppReducer'
import AppContextInitialState from 'src/types/AppContextInitialState'
import { AlertWarning } from './components/Alert'
import Loading from './components/Loading'

const initialState = {
  state: {
    isAuthenticated: false,
    token: null,
    user: null,
    loading: false,
    stripe: null,
  },
  dispatch: () => null,
}

const AppContext = React.createContext<{
  state: AppContextInitialState
  dispatch: React.Dispatch<AppActions>
}>(initialState)

export const AppProvider: React.FC = ({ children }) => {
  const [state, dispatch] = React.useReducer(appReducer, initialState.state)
  const navigate = useNavigate()

  const handleOnIdle = () => {
    if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
      dispatch({
        type: Types.Logout,
        payload: {},
      })

      AlertWarning("You've been disconnected due to inactivity", { autoClose: false })
      navigate('/login')
    }
  }

  // Change the last number to set the minutes
  useIdleTimer({
    timeout: 1000 * 60 * 60,
    onIdle: handleOnIdle,
    debounce: 500,
  })

  const initStripe = React.useCallback(async () => {
    const stripe = await loadStripe(config.stripeKey)
    dispatch({ type: Types.LoadStripe, payload: { stripe } })
  }, [])

  React.useEffect(() => {
    if (state.isAuthenticated && !state.stripe) initStripe()
  }, [state.isAuthenticated, state.stripe, initStripe])

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      <Loading useLoader={state.loading} />
      <ToastContainer
        position="bottom-left"
        autoClose={6000}
        hideProgressBar={false}
        newestOnTop={false}
        bodyClassName="custom-toast"
        closeOnClick
        rtl={false}
        draggable
        pauseOnFocusLoss
        pauseOnHover
        transition={Zoom}
      />
      <MuiPickersUtilsProvider utils={MomentUtils}>{children}</MuiPickersUtilsProvider>
    </AppContext.Provider>
  )
}

export const AppConsumer = AppContext.Consumer
export default AppContext
